/**
 * Group Device Management API Functions
 * Handles device assignment and removal from groups
 */

import { getAuthConfigFromStore, getAuthErrorMessage } from '../../auth/authUtils';
import { getApiGateway } from '../../api/endpointUtils';
import type {
  AddDevicesToGroupParams,
  AddDevicesToGroupResponse,
  DeviceGroupAssignment,
  RemoveDevicesFromGroupParams,
  RemoveDevicesFromGroupResponse,
  DeviceGroupRemoval,
  GetGroupDevicesParams,
  GetGroupDevicesResponse
} from '../types';

import { 
  ADD_DEVICES_TO_GROUP_ERROR_MESSAGES,
  REMOVE_DEVICES_FROM_GROUP_ERROR_MESSAGES,
  GET_GROUP_DEVICES_ERROR_MESSAGES
} from '../types';

/**
 * Add devices to group (Master privilege only)
 * Assigns one or more devices to specific groups
 * 
 * @param params - Device assignment parameters (Email and UserToken from AuthStore if not provided)
 * @returns Promise with device assignment response
 */
export async function addDevicesToGroup(params: Omit<AddDevicesToGroupParams, 'Email' | 'UserToken'> & {
  Email?: string;
  UserToken?: string;
}): Promise<AddDevicesToGroupResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: AddDevicesToGroupParams = {
      Email: authConfig.userEmail,
      UserToken: authConfig.userToken,
      TokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.Add || requestParams.Add.length === 0) {
      throw new Error('Device assignment list is required and cannot be empty');
    }
    
    // Validate each assignment item
    requestParams.Add.forEach((item, index) => {
      if (!item.PSN || item.PSN.trim().length === 0) {
        throw new Error(`PSN at index ${index} cannot be empty`);
      }
      if (!item.GroupID || item.GroupID.trim().length === 0) {
        throw new Error(`Group ID at index ${index} cannot be empty`);
      }
      if (!item.GroupName || item.GroupName.trim().length === 0) {
        throw new Error(`Group name at index ${index} cannot be empty`);
      }
    });

    // Make API request
    const response = await fetch(`${API_GATEWAY}/DashCam/PushMasterGroup`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: AddDevicesToGroupResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = ADD_DEVICES_TO_GROUP_ERROR_MESSAGES[data.resultcode as keyof typeof ADD_DEVICES_TO_GROUP_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Add a single device to a group
 * Convenience function for adding one device to a group
 * 
 * @param psn - Device PSN to add
 * @param groupId - Group management ID
 * @param groupName - Group name
 * @returns Promise with device assignment response
 */
export async function addSingleDeviceToGroup(
  psn: string, 
  groupId: string, 
  groupName: string
): Promise<AddDevicesToGroupResponse> {
  return addDevicesToGroup({
    Add: [{
      PSN: psn,
      GroupID: groupId,
      GroupName: groupName
    }]
  });
}

/**
 * Add multiple devices to groups
 * Convenience function for batch device assignments
 * 
 * @param assignments - Array of device group assignments
 * @returns Promise with device assignment response
 */
export async function addMultipleDevicesToGroups(assignments: DeviceGroupAssignment[]): Promise<AddDevicesToGroupResponse> {
  return addDevicesToGroup({
    Add: assignments
  });
}

/**
 * Remove devices from group (Master privilege only)
 * Removes one or more devices from specific groups
 * 
 * @param params - Device removal parameters (Email and UserToken from AuthStore if not provided)
 * @returns Promise with device removal response
 */
export async function removeDevicesFromGroup(params: Omit<RemoveDevicesFromGroupParams, 'Email' | 'UserToken'> & {
  Email?: string;
  UserToken?: string;
}): Promise<RemoveDevicesFromGroupResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: RemoveDevicesFromGroupParams = {
      Email: authConfig.userEmail,
      UserToken: authConfig.userToken,
      TokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.Del || requestParams.Del.length === 0) {
      throw new Error('Device removal list is required and cannot be empty');
    }
    
    // Validate each removal item
    requestParams.Del.forEach((item, index) => {
      if (!item.PSN || item.PSN.trim().length === 0) {
        throw new Error(`PSN at index ${index} cannot be empty`);
      }
      if (!item.GroupID || item.GroupID.trim().length === 0) {
        throw new Error(`Group ID at index ${index} cannot be empty`);
      }
      if (!item.GroupName || item.GroupName.trim().length === 0) {
        throw new Error(`Group name at index ${index} cannot be empty`);
      }
    });

    // Make API request
    const response = await fetch(`${API_GATEWAY}/DashCam/PopMasterGroup`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: RemoveDevicesFromGroupResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = REMOVE_DEVICES_FROM_GROUP_ERROR_MESSAGES[data.resultcode as keyof typeof REMOVE_DEVICES_FROM_GROUP_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Remove a single device from a group
 * Convenience function for removing one device from a group
 * 
 * @param psn - Device PSN to remove
 * @param groupId - Group management ID
 * @param groupName - Group name
 * @returns Promise with device removal response
 */
export async function removeSingleDeviceFromGroup(
  psn: string, 
  groupId: string, 
  groupName: string
): Promise<RemoveDevicesFromGroupResponse> {
  return removeDevicesFromGroup({
    Del: [{
      PSN: psn,
      GroupID: groupId,
      GroupName: groupName
    }]
  });
}

/**
 * Remove multiple devices from groups
 * Convenience function for batch device removals
 * 
 * @param removals - Array of device group removals
 * @returns Promise with device removal response
 */
export async function removeMultipleDevicesFromGroups(removals: DeviceGroupRemoval[]): Promise<RemoveDevicesFromGroupResponse> {
  return removeDevicesFromGroup({
    Del: removals
  });
}

/**
 * Get group devices information
 * Retrieves all groups with assigned devices and users (Master sees all, SubMaster sees assigned groups only)
 * 
 * @param params - Group devices parameters (Email and UserToken from AuthStore if not provided)
 * @returns Promise with group devices response
 */
export async function getGroupDevices(params: Omit<GetGroupDevicesParams, 'Email' | 'UserToken'> & {
  Email?: string;
  UserToken?: string;
} = {}): Promise<GetGroupDevicesResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: GetGroupDevicesParams = {
      Email: authConfig.userEmail,
      UserToken: authConfig.userToken,
      TokenType: 'web',
      ...params
    };

    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append('Email', requestParams.Email);
    queryParams.append('UserToken', requestParams.UserToken);
    queryParams.append('TokenType', requestParams.TokenType || 'web');

    // Make API request
    const response = await fetch(`${API_GATEWAY}/DashCam/GetAllocationDashCamList?${queryParams.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: GetGroupDevicesResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = GET_GROUP_DEVICES_ERROR_MESSAGES[data.resultcode as keyof typeof GET_GROUP_DEVICES_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}